import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Map, { Marker, MapRef, Source, Layer, GeolocateControl } from 'react-map-gl/mapbox';
import { useRepository } from "@city-sage/aurum-avis-labs-components";
import { useQuery } from "@tanstack/react-query";
import { QueryKeys } from "../../lib/QueryKeys";
import { ItineraryVisibilityEnum, Stop, Itinerary, StopProjection, ItineraryProjection } from "@city-sage/core-client";
import { MapPin, Layers, Map as MapIcon, Satellite } from 'lucide-react';
import { MAPBOX_ACCESS_TOKEN } from '../../lib/map/token';
import { useTranslation } from 'react-i18next';
import { Button } from '@city-sage/aurum-avis-labs-components';
import 'mapbox-gl/dist/mapbox-gl.css';
import { AggregatedStopProjection } from './StopPreviewSheet';


interface MapBounds {
  southWest: { lng: number; lat: number };
  northEast: { lng: number; lat: number };
}

export type ZoomMode = 'country' | 'city';

interface PublicStopsMapProps {
  stops?: StopProjection[];
  itineraries?: ItineraryProjection[];
  onBoundsChange?: (bounds: MapBounds) => void;
  onZoomChange?: (mode: ZoomMode, zoomLevel: number) => void;
  onMapReady?: (zoomToBounds: (bounds: MapBounds) => void) => void;
  onStopClick?: (aggregatedStop: AggregatedStopProjection) => void;
  onFilteredItinerariesChange?: (filteredItineraries: ItineraryProjection[]) => void;
  initialBounds?: MapBounds;
}

const PublicStopsMap: FC<PublicStopsMapProps> = ({ 
  stops, 
  itineraries,
  onBoundsChange, 
  onZoomChange, 
  onMapReady,
  onStopClick,
  onFilteredItinerariesChange,
  initialBounds 
}) => {
  const mapRef = useRef<MapRef | null>(null);
  const initialViewStateRef = useRef<any>(null);
  
  // Map style state
  const [mapStyle, setMapStyle] = useState<'streets' | 'satellite'>('satellite');
  
  // Current bounds state for filtering
  const [currentBounds, setCurrentBounds] = useState<MapBounds | null>(null);
  
  const mapStyleUrls = {
    streets: "mapbox://styles/mapbox/streets-v12",
    satellite: "mapbox://styles/mapbox/satellite-streets-v12"
  };
  
  const toggleMapStyle = useCallback(() => {
    setMapStyle(prev => prev === 'streets' ? 'satellite' : 'streets');
  }, []);

  // Calculate initial view state based on initialBounds or default values
  initialViewStateRef.current = useMemo(() => {
    if (initialBounds) {
      const centerLng = (initialBounds.southWest.lng + initialBounds.northEast.lng) / 2;
      const centerLat = (initialBounds.southWest.lat + initialBounds.northEast.lat) / 2;
      
      // Calculate zoom based on bounds size
      const lngDiff = Math.abs(initialBounds.northEast.lng - initialBounds.southWest.lng);
      const latDiff = Math.abs(initialBounds.northEast.lat - initialBounds.southWest.lat);
      const maxDiff = Math.max(lngDiff, latDiff);
      
      let zoom = 10;
      if (maxDiff < 0.01) zoom = 16;
      else if (maxDiff < 0.1) zoom = 13;
      else if (maxDiff < 1) zoom = 10;
      else if (maxDiff < 5) zoom = 8;
      else if (maxDiff < 20) zoom = 5;
      else zoom = 2;
      
      return {
        longitude: centerLng,
        latitude: centerLat,
        zoom
      };
    }
    
    return {
      longitude: 0,
      latitude: 20,
      zoom: 1
    };
  }, [initialBounds]);

  // Filter stops with valid coordinates and within current bounds
  const validStops = useMemo(() => {
    if (!stops) return [];
    
    let filteredStops = stops.filter(stopProjection => 
      stopProjection.placeCoordinatesLatitude && 
      stopProjection.placeCoordinatesLongitude &&
      !isNaN(stopProjection.placeCoordinatesLatitude) &&
      !isNaN(stopProjection.placeCoordinatesLongitude)
    );

    // Filter stops within current bounds if bounds are available
    if (currentBounds) {
      filteredStops = filteredStops.filter(stopProjection => {
        const lat = stopProjection.placeCoordinatesLatitude!;
        const lng = stopProjection.placeCoordinatesLongitude!;
        
        return lat >= currentBounds.southWest.lat && 
               lat <= currentBounds.northEast.lat && 
               lng >= currentBounds.southWest.lng && 
               lng <= currentBounds.northEast.lng;
      });
    }
    
    return filteredStops;
  }, [stops, currentBounds]);

  // Filter itineraries that have stops within the current bounds
  const filteredItineraries = useMemo(() => {
    if (!itineraries || !validStops || validStops.length === 0) return [];
    
    // Get unique itinerary IDs from the valid stops
    const stopItineraryIds = new Set(validStops.map(stopProjection => stopProjection.itineraryId));
    return itineraries.filter(itinerary => stopItineraryIds.has(itinerary.id));
  }, [itineraries, validStops]);

  // Call the filtered itineraries change callback when data changes
  useEffect(() => {
    if (onFilteredItinerariesChange && filteredItineraries) {
      onFilteredItinerariesChange(filteredItineraries);
    }
  }, [filteredItineraries, onFilteredItinerariesChange]);

  // Aggregate stops by place (same placeId) and convert to GeoJSON
  const stopsGeoJSON = useMemo(() => {
    if (!validStops || !itineraries) {
      return {
        type: 'FeatureCollection' as const,
        features: []
      };
    }
    
    const placeMap: Record<string, {
      location: {
        latitude: number;
        longitude: number;
        address?: {
          countryName: string;
          fullAddress: string;
        };
      };
      stopProjections: StopProjection[];
      itineraries: ItineraryProjection[];
    }> = {};
    
    validStops.forEach(stopProjection => {
      if (!stopProjection.placeCoordinatesLatitude || !stopProjection.placeCoordinatesLongitude || !stopProjection.placeId) return;
      
      // Use the placeId as the key for aggregation
      const placeId = stopProjection.placeId;
      
      // Find the itinerary for this stop
      const itinerary = itineraries?.find(it => it.id === stopProjection.itineraryId);
      if (!itinerary) return;
      
      if (placeMap[placeId]) {
        const existing = placeMap[placeId];
        existing.stopProjections.push(stopProjection);
        
        // Add itinerary if not already present
        if (!existing.itineraries.some(it => it.id === itinerary.id)) {
          existing.itineraries.push(itinerary);
        }
      } else {
        placeMap[placeId] = {
          location: {
            latitude: stopProjection.placeCoordinatesLatitude,
            longitude: stopProjection.placeCoordinatesLongitude,
            address: stopProjection.placeAddressCountryName ? {
              countryName: stopProjection.placeAddressCountryName,
              fullAddress: stopProjection.placeAddressCountryName
            } : undefined
          },
          stopProjections: [stopProjection],
          itineraries: [itinerary]
        };
      }
    });
    
    // Convert aggregated stops to GeoJSON features
    const features = Object.values(placeMap).map((aggregatedPlace, index) => ({
      type: 'Feature' as const,
      geometry: {
        type: 'Point' as const,
        coordinates: [aggregatedPlace.location.longitude, aggregatedPlace.location.latitude]
      },
      properties: {
        id: index,
        stopCount: aggregatedPlace.stopProjections.length,
        itineraryCount: aggregatedPlace.itineraries.length,
        aggregatedStop: JSON.stringify({
          location: aggregatedPlace.location,
          stopIds: aggregatedPlace.stopProjections.map(sp => sp.stopId).filter(Boolean),
          itineraryIds: aggregatedPlace.itineraries.map(it => it.id)
        }) // Store projection data for on-demand loading
      }
    }));
    
    return {
      type: 'FeatureCollection' as const,
      features
    };
  }, [validStops, itineraries]);

  // Handle cluster click to expand
  const handleClusterClick = useCallback((e: any) => {
    if (!mapRef.current) return;
    
    const feature = e.features[0];
    const clusterId = feature.properties.cluster_id;
    const map = mapRef.current.getMap();
    
    // Force city mode when clicking on any location
    if (onZoomChange) {
      onZoomChange('city', map.getZoom());
    }
    
    // Get the source and expand the cluster
    const source = map.getSource('stops') as mapboxgl.GeoJSONSource;
    if (source && source.getClusterExpansionZoom) {
      source.getClusterExpansionZoom(clusterId, (err: any, zoom: number | null | undefined) => {
        if (err || zoom === null || zoom === undefined) return;
        
        map.easeTo({
          center: feature.geometry.coordinates,
          zoom: zoom,
          duration: 500
        });
      });
    }
  }, [onZoomChange]);

  // Handle unclustered point click
  const handlePointClick = useCallback((e: any) => {
    // Force city mode when clicking on any location
    if (onZoomChange && mapRef.current) {
      const map = mapRef.current.getMap();
      onZoomChange('city', map.getZoom());
    }
    
    if (!onStopClick) return;
    
    const feature = e.features[0];
    const aggregatedStopData = feature.properties.aggregatedStop;
    
    try {
      const aggregatedStop: AggregatedStopProjection = JSON.parse(aggregatedStopData);
      onStopClick(aggregatedStop);
    } catch (error) {
      console.error('Error parsing aggregated stop data:', error);
    }
  }, [onStopClick, onZoomChange]);

  // Handle map movement and bounds change
  const handleMove = useCallback(() => {
    if (mapRef.current) {
      const map = mapRef.current.getMap();
      const bounds = map.getBounds();
      
      if (bounds) {
        const mapBounds: MapBounds = {
          southWest: { 
            lng: bounds.getWest(), 
            lat: bounds.getSouth() 
          },
          northEast: { 
            lng: bounds.getEast(), 
            lat: bounds.getNorth() 
          }
        };
        
        // Update internal bounds state for filtering
        setCurrentBounds(mapBounds);
        
        // Also call the parent callback if provided
        if (onBoundsChange) {
          onBoundsChange(mapBounds);
        }
      }
    }
  }, [onBoundsChange]);

  // Zoom to bounds function
  const zoomToBoundsFunction = useCallback((bounds: MapBounds) => {
    if (mapRef.current && bounds && bounds.southWest && bounds.northEast) {
      const map = mapRef.current.getMap();
      
      // fitBounds expects [west, south, east, north] format
      const boundsArray: [number, number, number, number] = [
        bounds.southWest.lng, // west
        bounds.southWest.lat, // south
        bounds.northEast.lng, // east
        bounds.northEast.lat  // north
      ];
      
      map.fitBounds(
        boundsArray,
        {
          padding: {
            top: 50,
            bottom: 200, // Extra padding for cards at bottom
            left: 50,
            right: 50
          },
          duration: 1500,
          maxZoom: 10
        }
      );
    }
  }, []);

  // Handle map load and emit initial bounds
  const handleLoad = useCallback(() => {
    if (mapRef.current) {
      const map = mapRef.current.getMap();
      const bounds = map.getBounds();
      
      if (bounds) {
        const mapBounds: MapBounds = {
          southWest: { 
            lng: bounds.getWest(), 
            lat: bounds.getSouth() 
          },
          northEast: { 
            lng: bounds.getEast(), 
            lat: bounds.getNorth() 
          }
        };
        
        // Update internal bounds state for filtering
        setCurrentBounds(mapBounds);
        
        // Also call the parent callback if provided
        if (onBoundsChange) {
          onBoundsChange(mapBounds);
        }
      }
    }

    // Pass zoom function to parent when map is ready
    if (onMapReady) {
      onMapReady(zoomToBoundsFunction);
    }
  }, [onBoundsChange, onMapReady, zoomToBoundsFunction]);

  // Handle zoom changes and determine mode
  const handleZoom = useCallback(() => {
    if (mapRef.current && onZoomChange) {
      const map = mapRef.current.getMap();
      const zoomLevel = map.getZoom();
      
      // Determine mode based on zoom level
      const mode: ZoomMode = zoomLevel < 5 ? 'country' : 'city';
      
      onZoomChange(mode, zoomLevel);
    }
  }, [onZoomChange]);

  // Cluster layer configuration
  const clusterLayer: any = {
    id: 'clusters',
    type: 'circle',
    source: 'stops',
    filter: ['has', 'point_count'],
    paint: {
      'circle-color': [
        'step',
        ['get', 'point_count'],
        'hsl(210.3, 61.2%, 32.4%)',  // Primary color for small clusters
        10,
        'hsl(210.3, 61.2%, 42.4%)',  // Lighter primary for medium clusters
        30,
        'hsl(210.3, 61.2%, 52.4%)'   // Even lighter primary for large clusters
      ],
      'circle-radius': [
        'step',
        ['get', 'point_count'],
        18,  // Increased radius for small clusters (2-9 points) - better touch target
        10,
        24,  // Increased radius for medium clusters (10-29 points)
        30,
        30   // Increased radius for large clusters (30+ points)
      ],
      'circle-stroke-width': 2,
      'circle-stroke-color': '#fff'
    }
  };

  // Cluster count layer configuration
  const clusterCountLayer: any = {
    id: 'cluster-count',
    type: 'symbol',
    source: 'stops',
    filter: ['has', 'point_count'],
    layout: {
      'text-field': ['get', 'point_count_abbreviated'],
      'text-size': 12
    },
    paint: {
      'text-color': '#ffffff'
    }
  };

  // Unclustered point layer configuration
  const unclusteredPointLayer: any = {
    id: 'unclustered-point',
    type: 'circle',
    source: 'stops',
    filter: ['!', ['has', 'point_count']],
    paint: {
      'circle-color': 'hsl(210.3, 61.2%, 32.4%)',
      'circle-radius': 12,  // Increased radius for better touch target
      'circle-stroke-width': 2,
      'circle-stroke-color': '#fff'
    }
  };

  return (
    <div className="w-full h-[calc(100dvh-40px-36px)] relative overflow-hidden">
      <Map
        ref={mapRef}
        mapboxAccessToken={MAPBOX_ACCESS_TOKEN}
        style={{ width: '100%', height: '100%', }}
        mapStyle={mapStyleUrls[mapStyle]}
        initialViewState={initialViewStateRef.current}
        onLoad={handleLoad}
        onMove={handleMove}
        onZoom={handleZoom}
        onClick={(e) => {
          // Handle clicks on clusters and unclustered points
          const map = mapRef.current?.getMap();
          if (!map) return;
          
          const features = map.queryRenderedFeatures(e.point, {
            layers: ['clusters', 'unclustered-point']
          });
          
          if (features.length > 0) {
            const feature = features[0];
            
            if (feature.layer?.id === 'clusters') {
              handleClusterClick({ features: [feature] });
            } else if (feature.layer?.id === 'unclustered-point') {
              handlePointClick({ features: [feature] });
            }
          }
        }}
        onMouseEnter={(e) => {
          const map = mapRef.current?.getMap();
          if (!map) return;
          
          const features = map.queryRenderedFeatures(e.point, {
            layers: ['clusters', 'unclustered-point']
          });
          
          if (features.length > 0) {
            map.getCanvas().style.cursor = 'pointer';
          }
        }}
        onMouseLeave={() => {
          const map = mapRef.current?.getMap();
          if (map) {
            map.getCanvas().style.cursor = '';
          }
        }}
      >
        {/* GeoJSON source with clustering enabled */}
        <Source
          id="stops"
          type="geojson"
          data={stopsGeoJSON}
          cluster={true}
          clusterMaxZoom={14} // Max zoom to cluster points on
          clusterRadius={50}  // Radius of each cluster when clustering points
        >
          {/* Cluster circles */}
          <Layer {...clusterLayer} />
          
          {/* Cluster count labels */}
          <Layer {...clusterCountLayer} />
          
          {/* Unclustered points */}
          <Layer {...unclusteredPointLayer} />
        </Source>
        
        {/* Map Style Toggle */}
        <div className="absolute top-10 p-4 -right-1 z-10 cursor-pointer" onClick={toggleMapStyle}>
        {mapStyle === 'streets' ? (
              <Satellite className="h-6 w-6 text-white" />
            ) : (
              <MapIcon className="h-6 w-6 text-white" />
            )}
        </div>

        {/* Geolocation Control */}
        <GeolocateControl
          position="top-right"
          positionOptions={{ enableHighAccuracy: true }}
          trackUserLocation={true}
          showAccuracyCircle={false}
        />
      </Map>
    </div>
  );
};

export default PublicStopsMap; 